import { useState } from 'react'
import type { Answer, AnswerMap, AnswerValue, FormDefinition } from 'app/lib/forms/types'
import { toAnswerArray, validate, visibleQuestions } from 'app/lib/forms/formLogic'
import { QuestionField } from './QuestionField'

type Props = {
  form: FormDefinition
  initialAnswers?: AnswerMap
  submitLabel: string
  onSubmit: (answers: Answer[]) => void
}

export function FormRenderer({ form, initialAnswers, submitLabel, onSubmit }: Props) {
  const [answers, setAnswers] = useState<AnswerMap>(initialAnswers ?? {})
  const [errors, setErrors] = useState<Record<string, string>>({})

  const change = (questionId: string, value: AnswerValue) => {
    const next = { ...answers, [questionId]: value }
    setAnswers(next)
    // Only revalidate fields already showing an error, so nobody is corrected mid-keystroke.
    // Take the fresh message rather than keeping the old one: a blank required field that gets
    // filled in badly has fixed one problem and created another, and the message must follow.
    if (Object.keys(errors).length === 0) return
    const revalidated = validate(form, next)
    setErrors(
      Object.fromEntries(
        Object.keys(errors)
          .filter((id) => revalidated[id])
          .map((id) => [id, revalidated[id]]),
      ),
    )
  }

  const submit = () => {
    const found = validate(form, answers)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    onSubmit(toAnswerArray(form, answers))
  }

  return (
    <div className="theme-light text-strong flex flex-col gap-6">
      {visibleQuestions(form, answers).map((question) => (
        <QuestionField
          key={question.id}
          question={question}
          value={answers[question.id]}
          error={errors[question.id]}
          onChange={(value) => change(question.id, value)}
        />
      ))}

      <button
        type="button"
        onClick={submit}
        className="w-full rounded-lg bg-[var(--form-accent)] px-4 py-3 text-base font-medium text-[var(--form-accent-fg)]"
      >
        {submitLabel}
      </button>
    </div>
  )
}
